import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      setError(e?.response?.data?.error?.message ?? 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center paper-bg p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-navy-800 flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8740C" strokeWidth="2">
                <path d="M3 21h18M5 21V8l7-5 7 5v13M9 21v-7h6v7" />
              </svg>
            </div>
            <div className="text-left">
              <div className="text-xs uppercase tracking-widest text-navy-800/70 font-medium">
                Government of Uttar Pradesh
              </div>
              <div className="font-hindi text-sm text-navy-800/80 leading-tight">पशुधन विभाग</div>
            </div>
          </div>
          <h1 className="serif-display text-3xl text-navy-800 font-medium leading-tight">
            Unified Control
            <br />
            Command Centre
          </h1>
          <p className="text-sm text-navy-800/60 mt-3">Department of Animal Husbandry</p>
        </div>

        <div className="bg-white border border-cream-200 rounded-sm shadow-sm">
          <div className="px-8 py-8">
            <div className="mb-6">
              <h2 className="serif-display text-lg text-navy-800 accent-underline inline-block">Sign in</h2>
              <p className="text-xs text-navy-800/60 mt-3">
                Use your official credentials issued by the System Administrator.
              </p>
            </div>

            <form className="space-y-4" onSubmit={onSubmit}>
              <div>
                <label htmlFor="username" className="block text-xs uppercase tracking-wider text-navy-800/70 mb-1.5 font-medium">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-3 py-2.5 border border-cream-300 rounded-sm text-sm focus:outline-none focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500 bg-cream-50"
                />
              </div>

              <div>
                <div className="flex justify-between items-baseline mb-1.5">
                  <label htmlFor="password" className="block text-xs uppercase tracking-wider text-navy-800/70 font-medium">
                    Password
                  </label>
                  <a href="#" className="text-xs text-saffron-600 hover:text-saffron-700">
                    Forgot?
                  </a>
                </div>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 border border-cream-300 rounded-sm text-sm focus:outline-none focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500 bg-cream-50"
                />
              </div>

              {error && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full bg-saffron-500 hover:bg-saffron-600 disabled:opacity-60 text-white py-3 text-sm font-medium tracking-wide transition rounded-sm"
              >
                {busy ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
          </div>

          <div className="px-8 py-4 border-t border-cream-200 bg-cream-50 text-xs text-navy-800/60 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>Secured by JWT &amp; bcrypt. Session activity audited.</span>
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-navy-800/50">
          <div>Augurs Technologies Pvt. Ltd. · For Internal Government Use</div>
          <div className="mt-1">Empanelled with UPDESCO · CMMI Level 3 · ISO 27001</div>
        </div>
      </div>
    </div>
  );
}
